import { Link } from "@tanstack/react-router";

import { useI18n, type MessageKey } from "~/lib/i18n";
import { runsAsApp, useInstall } from "~/lib/install";

type FooterLink =
  | {
      label: MessageKey;
      to: "/fare-check" | "/book" | "/tickets" | "/v" | "/complaints/new" | "/app" | "/operator" | "/console" | "/developers" | "/open-data";
      /** Left out where the site already runs as an app. */
      browserOnly?: boolean;
    }
  // The Demo Wallet is a separate app, so the router mustn't handle it.
  | { label: MessageKey; href: string };

const groups: { title: MessageKey; links: FooterLink[] }[] = [
  {
    title: "footerPassengers",
    links: [
      { label: "fareCheck", to: "/fare-check" },
      { label: "bookSeat", to: "/book" },
      { label: "navTickets", to: "/tickets" },
      { label: "verifyVehicle", to: "/v" },
      { label: "reportProblem", to: "/complaints/new" },
      { label: "installApp", to: "/app", browserOnly: true },
    ],
  },
  { title: "footerOperators", links: [{ label: "navOperator", to: "/operator" }] },
  { title: "footerGovernment", links: [{ label: "consoleNav", to: "/console" }] },
  {
    title: "footerPartners",
    links: [
      { label: "footerDemoWallet", href: "/wallet/" },
      { label: "partnerApi", to: "/developers" },
      { label: "openData", to: "/open-data" },
    ],
  },
];

const linkClass = "hover:text-ink hover:underline dark:hover:text-ink-dark";

function FooterLinkItem({ link }: { link: FooterLink }) {
  const { t } = useI18n();

  return "href" in link ? (
    <a href={link.href} className={linkClass}>
      {t(link.label)}
    </a>
  ) : (
    <Link to={link.to} className={linkClass}>
      {t(link.label)}
    </Link>
  );
}

/** Every page's footer. Bottom padding keeps it clear of the floating demo controls. */
export function SiteFooter() {
  const { t } = useI18n();
  const asApp = runsAsApp(useInstall().state);

  return (
    <footer className="mt-10 border-t border-slate-200 dark:border-slate-800">
      <div className="mx-auto max-w-5xl px-4 pb-20 pt-8 text-sm">
        <nav aria-label={t("footerLabel")} className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          {groups.map((g) => (
            <section key={g.title}>
              <h2 className="font-semibold text-ink dark:text-ink-dark">{t(g.title)}</h2>
              <ul className="mt-2 space-y-1.5 text-slate-700 dark:text-slate-300">
                {g.links.filter((l) => !(asApp && "browserOnly" in l && l.browserOnly)).map((l) => (
                  <li key={l.label}>
                    <FooterLinkItem link={l} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
        <div className="mt-8 space-y-1 border-t border-slate-200 pt-4 text-slate-600 dark:border-slate-800 dark:text-slate-400">
          <p className="font-semibold text-slate-800 dark:text-slate-200">{t("appName")}</p>
          <p>{t("systemName")}</p>
          <p>{t("footerNotice")}</p>
        </div>
      </div>
    </footer>
  );
}
